import React, { useRef, useEffect } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';

interface AnimatedSectionProps {
    children: React.ReactNode;
    className?: string;
    animation?: 'fadeInUp' | 'slideInLeft' | 'slideInRight' | 'scaleIn' | 'bounceIn' | 'rotateIn' | 'flipIn';
    delay?: number;
    duration?: number;
    threshold?: number;
    once?: boolean;
}

const animationVariants = {
    fadeInUp: {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0 }
    },
    slideInLeft: {
        hidden: { opacity: 0, x: -100 },
        visible: { opacity: 1, x: 0 }
    },
    slideInRight: {
        hidden: { opacity: 0, x: 100 },
        visible: { opacity: 1, x: 0 }
    },
    scaleIn: {
        hidden: { opacity: 0, scale: 0.8 },
        visible: { opacity: 1, scale: 1 }
    },
    bounceIn: {
        hidden: { opacity: 0, scale: 0.3 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 10
            }
        }
    },
    rotateIn: {
        hidden: { opacity: 0, rotate: -180, scale: 0.5 },
        visible: {
            opacity: 1,
            rotate: 0,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 200,
                damping: 15
            }
        }
    },
    flipIn: {
        hidden: { opacity: 0, rotateY: -90 },
        visible: {
            opacity: 1,
            rotateY: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    }
};

const AnimatedSection: React.FC<AnimatedSectionProps> = ({
    children,
    className = '',
    animation = 'fadeInUp',
    delay = 0,
    duration = 0.6,
    threshold = 0.1,
    once = true
}) => {
    const ref = useRef(null);
    const isInView = useInView(ref, {
        amount: threshold,
        once,
        margin: "-100px 0px -100px 0px"
    });
    const controls = useAnimation();

    useEffect(() => {
        if (isInView) {
            controls.start('visible');
        } else if (!once) {
            controls.start('hidden');
        }
    }, [isInView, controls, once]);

    const variant = animationVariants[animation];

    return (
        <motion.div
            ref={ref}
            initial="hidden"
            animate={controls}
            variants={variant}
            transition={{
                duration,
                delay,
                ease: "easeOut"
            }}
            className={className}
        >
            {children}
        </motion.div>
    );
};

export default AnimatedSection; 
